import { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { skillsData } from '../data/skills';

const SkillsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = [
    { value: 'all', label: 'All Skills' },
    { value: 'frontend', label: 'Frontend' },
    { value: 'backend', label: 'Backend' },
    { value: 'database', label: 'Database' },
    { value: 'devops', label: 'DevOps' },
    { value: 'mobile', label: 'Mobile' },
    { value: 'desktop', label: 'Desktop' },
    { value: 'ai-ml', label: 'AI & ML' },
    { value: 'other', label: 'Other' }
  ];

  const filteredSkills = selectedCategory === 'all' 
    ? skillsData 
    : skillsData.filter(skill => skill.category === selectedCategory);

  const chartData = filteredSkills.map(skill => ({
    name: skill.name,
    level: skill.level,
    category: skill.category
  }));

  const getSkillLevel = (level: number) => {
    if (level >= 90) return 'Expert';
    if (level >= 80) return 'Advanced';
    if (level >= 60) return 'Intermediate';
    if (level >= 40) return 'Beginner';
    return 'Novice';
  };

  const getSkillColor = (level: number) => {
    if (level >= 90) return 'text-green-600';
    if (level >= 80) return 'text-blue-600';
    if (level >= 60) return 'text-yellow-600';
    if (level >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-12"
      >
        <h1 className="text-4xl font-bold mb-4">Skills</h1>
        <p className="text-xl text-muted-foreground">
          My technical expertise across various domains and technologies.
        </p>
      </motion.div>

      {/* Category Filter */}
      <div className="mb-8 sticky top-20 z-10 bg-background/80 backdrop-blur-md rounded-lg shadow-sm transition-all">
        <h2 className="text-2xl font-semibold mb-4">Filter by Category</h2>
        <div className="flex flex-wrap gap-2">
          {categories.map(category => (
            <button
              key={category.value}
              onClick={() => setSelectedCategory(category.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                selectedCategory === category.value
                  ? 'bg-primary-600 text-white shadow-md scale-105'
                  : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200 dark:bg-secondary-800 dark:text-secondary-300 dark:hover:bg-secondary-700'
              }`}
              tabIndex={0}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* Skills Chart */}
      <div className="mb-12">
        <h2 className="text-2xl font-semibold mb-6">Skills Overview</h2>
        <div className="card p-6 glass-card">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 40, right: 20, top: 20, bottom: 20 }}>
              <defs>
                <linearGradient id="bar3d-gradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#38bdf8" />
                  <stop offset="70%" stopColor="#0ea5e9" />
                  <stop offset="100%" stopColor="#0369a1" />
                </linearGradient>
                <filter id="bar3d-shadow" x="-20%" y="-20%" width="140%" height="140%">
                  <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#0ea5e9" floodOpacity="0.18" />
                </filter>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, 100]} />
              <YAxis 
                type="category" 
                dataKey="name" 
                width={180} 
                tick={{ fontSize: 13, fontFamily: 'Inter, sans-serif' }}
              />
              <Tooltip 
                formatter={(value: number) => [`${value}%`, 'Proficiency']}
                labelFormatter={(label: string) => `Skill: ${label}`}
              />
              <Bar dataKey="level" fill="url(#bar3d-gradient)" barSize={18} radius={[8, 8, 8, 8]} filter="url(#bar3d-shadow)">
                {chartData.map((_, idx) => (
                  <Cell key={`cell-${idx}`} className="transition-all duration-700 hover:scale-x-105" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Skills Grid */}
      <div className="mb-12">
        <h2 className="text-2xl font-semibold mb-6">Detailed Skills</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSkills.map((skill, index) => (
            <motion.div
              key={skill.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="card glass-card hover:shadow-xl hover:scale-[1.025] focus-within:shadow-xl focus-within:scale-[1.025] transition-all duration-300 outline-none"
              tabIndex={0}
            >
              <div className="card-content">
                <div className="flex items-center justify-between mb-4 mt-2">
                  <h3 className="card-title text-lg">{skill.name}</h3>
                  <span className={`text-sm font-medium ${getSkillColor(skill.level)}`}>
                    {getSkillLevel(skill.level)}
                  </span>
                </div>
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-muted-foreground mb-1">
                    <span>Proficiency</span>
                    <span>{skill.level}%</span>
                  </div>
                  <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-2">
                    <motion.div
                      className="h-2 rounded-full shadow-lg relative overflow-visible"
                      initial={{ width: 0 }}
                      animate={{ width: `${skill.level}%` }}
                      transition={{ duration: 0.7, delay: index * 0.1 }}
                      style={{
                        background: 'linear-gradient(90deg, #38bdf8 0%, #0ea5e9 70%, #0369a1 100%)',
                        boxShadow: '0 2px 8px 0 rgba(14,165,233,0.18), 0 1px 0 0 #fff6 inset'
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          width: '100%',
                          height: '60%',
                          borderRadius: '8px',
                          background: 'linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.05) 100%)',
                          pointerEvents: 'none',
                          zIndex: 1
                        }}
                      />
                    </motion.div>
                  </div>
                </div>
                {skill.description && (
                  <p className="text-sm text-muted-foreground mt-2 mb-3">
                    {skill.description}
                  </p>
                )}
                <div className="mt-2">
                  <span className="bg-secondary-100 dark:bg-secondary-800 text-xs px-2 py-1 rounded capitalize">
                    {skill.category}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Skills Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="card text-center"
        >
          <div className="card-content">
            <h3 className="text-2xl font-bold text-primary-600 mb-2">
              {skillsData.length}
            </h3>
            <p className="text-muted-foreground">Total Skills</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="card text-center"
        >
          <div className="card-content">
            <h3 className="text-2xl font-bold text-green-600 mb-2">
              {skillsData.filter(skill => skill.level >= 80).length}
            </h3>
            <p className="text-muted-foreground">Advanced Level</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="card text-center"
        >
          <div className="card-content">
            <h3 className="text-2xl font-bold text-blue-600 mb-2">
              {Math.round(skillsData.reduce((acc, skill) => acc + skill.level, 0) / skillsData.length)}
            </h3>
            <p className="text-muted-foreground">Average Proficiency</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SkillsPage; 